/**
 * PricingService — Centralized Pricing Module
 * GRIT-4652: Single source of truth for discount calculation, price validation,
 * and frontend/backend reconciliation.
 *
 * Architecture:
 *   1. Liquid (pricing-logic.liquid) computes server-side prices and embeds them
 *      as data attributes on <price-list> elements.
 *   2. This module reads those data attributes and applies client-side adjustments
 *      based on customer state (logged in, promo cookie, etc.).
 *   3. Validation layer asserts frontend displayed prices match backend source data
 *      and logs mismatches to the console as warnings.
 *
 * Data flow:
 *   Shopify Liquid (server) --> data-* attributes on <price-list>
 *       --> PricingService.applyPricing() (client)
 *           --> DOM updates (sale-price, compare-at-price elements)
 *           --> PricingService.validate() (assertion + alert on mismatch)
 */
var PricingService = (function () {
  'use strict';

  // ---------------------------------------------------------------------------
  // Internal helpers
  // ---------------------------------------------------------------------------

  /**
   * Check if the current customer has a valid promo entitlement
   * (logged-in customer OR discount cookie present).
   */
  function hasPromoEntitlement() {
    var customerId = '';
    if (typeof theme !== 'undefined' && theme && theme.settings) {
      customerId = theme.settings.customer_id || '';
    }
    var isLoggedIn = customerId !== '' && customerId !== '0';
    var hasCookie = _getCookie('discount_code');
    if (hasCookie) {
      try {
        if (typeof window !== 'undefined' && window.localStorage) {
          window.localStorage.setItem('discount_code', hasCookie);
        }
      } catch (e) { /* ignore */ }
    } else {
      try {
        if (typeof window !== 'undefined' && window.localStorage) {
          hasCookie = window.localStorage.getItem('discount_code');
        }
      } catch (e) {
        hasCookie = null;
      }
    }
    return isLoggedIn || !!hasCookie;
  }

  /**
   * Read a cookie by name.
   */
  function _getCookie(name) {
    var match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
    return match ? match[2] : null;
  }

  /**
   * Parse a money string to cents (integer). Strips currency symbols and whitespace,
   * handles comma/period decimal separators.
   */
  function parseMoney(value) {
    if (typeof value === 'number') return value;
    if (!value) return 0;
    var cleaned = String(value).replace(/[^\d.,-]/g, '').trim();
    // Handle European format (1.234,56) vs US format (1,234.56)
    if (cleaned.indexOf(',') > cleaned.indexOf('.')) {
      cleaned = cleaned.replace(/\./g, '').replace(',', '.');
    } else {
      cleaned = cleaned.replace(/,/g, '');
    }
    return Math.round(parseFloat(cleaned) * 100) || 0;
  }

  // ---------------------------------------------------------------------------
  // DOM manipulation helpers
  // ---------------------------------------------------------------------------

  /**
   * Remove stray text nodes inside price elements. The theme's built-in variant
   * switcher appends raw text nodes — this cleans them so .pl-price is the
   * single source of displayed text.
   */
  function cleanPriceElement(elem) {
    if (!elem) return;
    elem.childNodes.forEach(function (node) {
      if (node.nodeType === Node.TEXT_NODE) {
        node.remove();
      }
    });
    // Ensure placeholder span exists for theme's variant switcher
    if (!elem.querySelector('.price_last_span')) {
      elem.insertAdjacentHTML('beforeend', '<span class="price_last_span"></span>');
    }
  }

  /**
   * Set the text content of the .pl-price element within a parent.
   * Creates the element if it does not exist.
   */
  function setPriceText(parentElem, text) {
    if (!parentElem) return;
    var plPrice = parentElem.querySelector('.pl-price');
    if (!plPrice) {
      parentElem.insertAdjacentHTML('afterbegin', '<span class="pl-price"></span>');
      plPrice = parentElem.querySelector('.pl-price');
    }
    plPrice.textContent = text;
  }

  // ---------------------------------------------------------------------------
  // Core pricing logic
  // ---------------------------------------------------------------------------

  /**
   * Apply pricing to a single <price-list> element.
   * Reads data attributes set by the Liquid pricing-logic snippet and adjusts
   * the display based on customer promo entitlement.
   *
   * Data attributes consumed:
   *   data-variant-price          — variant price in cents
   *   data-compare-at-price       — compare-at price in cents
   *   data-discount-variant-price — private sale price (formatted money string)
   *   data-discount-compare-price — compare-at for private sale (formatted money string)
   *   data-do-sale-price          — "true" if private sale applies server-side
   *   data-product-id             — product ID
   */
  function applyPricing(priceListElem) {
    if (!priceListElem) return;

    var salePriceElem = priceListElem.querySelector('sale-price');
    var compareAtElem = priceListElem.querySelector('compare-at-price');
    if (!salePriceElem) return;

    var discountVariantPrice = priceListElem.getAttribute('data-discount-variant-price');
    var discountComparePrice = priceListElem.getAttribute('data-discount-compare-price');
    var doSalePrice = priceListElem.getAttribute('data-do-sale-price') === 'true';

    if (!discountVariantPrice || !discountComparePrice) return;

    cleanPriceElement(salePriceElem);
    cleanPriceElement(compareAtElem);

    var entitled = hasPromoEntitlement();

    if (!entitled) {
      // Customer is not entitled to private sale — show the compare-at price
      // as the main price and hide the strikethrough.
      setPriceText(salePriceElem, discountComparePrice);
      salePriceElem.classList.remove('text-on-sale');
      salePriceElem.classList.add('text-subdued');
      if (compareAtElem) {
        compareAtElem.classList.add('hidden');
        compareAtElem.setAttribute('hidden', 'true');
      }
    } else {
      // Customer is entitled — show discounted price as sale, original as compare-at.
      setPriceText(salePriceElem, discountVariantPrice);
      salePriceElem.classList.remove('text-subdued');
      salePriceElem.classList.add('text-on-sale');

      if (!compareAtElem) {
        priceListElem.insertAdjacentHTML(
          'beforeend',
          '<compare-at-price class="text-subdued line-through"><span class="pl-price"></span><span class="price_last_span"></span></compare-at-price>'
        );
        compareAtElem = priceListElem.querySelector('compare-at-price');
      } else {
        compareAtElem.classList.remove('hidden');
        compareAtElem.removeAttribute('hidden');
      }
      setPriceText(compareAtElem, discountComparePrice);
    }

    // If sale and compare-at display the same value, hide the compare-at
    var saleText = (salePriceElem.querySelector('.pl-price') || {}).textContent || '';
    var compareText = compareAtElem ? (compareAtElem.querySelector('.pl-price') || {}).textContent || '' : '';
    if (saleText && saleText === compareText && compareAtElem) {
      compareAtElem.classList.add('hidden');
      compareAtElem.setAttribute('hidden', 'true');
      if (doSalePrice) {
        salePriceElem.classList.remove('text-on-sale');
      }
    }
  }

  /**
   * Apply pricing to all <price-list> elements on the page.
   */
  function applyAll() {
    document.querySelectorAll('price-list').forEach(function (pl) {
      applyPricing(pl);
    });
  }

  /**
   * Apply pricing within a specific container (e.g., a quick-buy modal).
   */
  function applyWithinContainer(container) {
    if (!container) return;
    container.querySelectorAll('price-list').forEach(function (pl) {
      applyPricing(pl);
    });
  }

  // ---------------------------------------------------------------------------
  // Variant change handler
  // ---------------------------------------------------------------------------

  /**
   * Called when a variant changes. Clears displayed prices so Liquid can re-render,
   * then re-applies client-side pricing logic.
   *
   * @param {string} productId - The product ID
   * @param {Element|null} quickBuyElem - Optional quick-buy modal container
   * @param {string|null} handle - Optional product handle for quick-buy lookup
   */
  function onVariantChange(productId, quickBuyElem, handle) {
    var entitled = hasPromoEntitlement();

    // Handle quick-buy modals by handle
    if (handle) {
      document.querySelectorAll('quick-buy-modal').forEach(function (qb) {
        if (qb.getAttribute('handle') && qb.getAttribute('handle').includes(handle)) {
          qb.querySelectorAll('price-list').forEach(function (pl) {
            applyPricing(pl);
          });
        }
      });
    }

    // Handle explicit quick-buy element
    if (quickBuyElem) {
      quickBuyElem.querySelectorAll('price-list').forEach(function (pl) {
        applyPricing(pl);
      });
    }

    // Handle all product page price lists (multiple may exist: sticky bar, main PDP, etc.)
    document.querySelectorAll('price-list[data-product-id="' + productId + '"]').forEach(function (pl) {
      applyPricing(pl);
    });

    // Fill empty prices on PDP
    fillEmptyPrices();
  }

  // ---------------------------------------------------------------------------
  // Empty price recovery
  // ---------------------------------------------------------------------------

  /**
   * On PDP, if a sale-price .pl-price is empty after Liquid render (race condition
   * with variant switcher), populate it from the data attributes.
   */
  function fillEmptyPrices() {
    if (window.location.href.indexOf('/products') === -1) return;

    document.querySelectorAll('price-list').forEach(function (priceList) {
      var salePriceElem = priceList.querySelector('sale-price');
      if (!salePriceElem) return;
      var salePricePl = salePriceElem.querySelector('.pl-price');
      if (!salePricePl) return;

      var compareAtElem = priceList.querySelector('compare-at-price');
      if (!compareAtElem) return;
      var compareAtPrice = parseInt(priceList.getAttribute('data-compare-at-price') || '0', 10);
      var variantPrice = parseInt(priceList.getAttribute('data-variant-price') || '0', 10);
      if (compareAtElem && compareAtPrice > 0 && compareAtPrice > variantPrice) {
        compareAtElem.classList.remove('hidden');
        compareAtElem.removeAttribute('hidden');
      }
      var compareAtPl = compareAtElem.querySelector('.pl-price');
      if (!compareAtPl) return;

      var doSalePrice = priceList.getAttribute('data-do-sale-price') === 'true';
      var discountVariantPrice = priceList.getAttribute('data-discount-variant-price');
      var discountComparePrice = priceList.getAttribute('data-discount-compare-price');

      // If sale price is empty, fill from data attributes
      if (doSalePrice && salePricePl.textContent.trim() === '') {
        cleanPriceElement(compareAtElem);
        cleanPriceElement(salePriceElem);
        if (discountComparePrice && discountVariantPrice) {
          salePricePl.textContent = discountVariantPrice;
          compareAtPl.textContent = discountComparePrice;
        }
      }

      // Non-entitled: revert to compare-at as main price
      if (!hasPromoEntitlement()) {
        if (discountComparePrice) {
          salePricePl.textContent = discountComparePrice;
        }
        if (doSalePrice || salePricePl.textContent === compareAtPl.textContent) {
          compareAtElem.classList.add('hidden');
          compareAtElem.setAttribute('hidden', 'true');
        }
      }

      // Hide compare-at if both prices match
      if (salePricePl.textContent === compareAtPl.textContent) {
        compareAtElem.classList.add('hidden');
        compareAtElem.setAttribute('hidden', 'true');
        if (doSalePrice) {
          salePriceElem.classList.remove('text-on-sale');
        }
      }
    });
  }

  // ---------------------------------------------------------------------------
  // Validation / Reconciliation layer
  // ---------------------------------------------------------------------------

  /**
   * Validate that displayed prices match the source data attributes.
   * Logs warnings to console when mismatches are detected.
   *
   * @returns {Array} Array of mismatch objects, empty if all valid.
   */
  function validate() {
    var mismatches = [];
    var entitled = hasPromoEntitlement();

    document.querySelectorAll('price-list').forEach(function (priceList) {
      var productId = priceList.getAttribute('data-product-id') ||
                      (priceList.querySelector('.product-id') || {}).value || 'unknown';
      var salePriceElem = priceList.querySelector('sale-price');
      if (!salePriceElem) return;

      var displayedPrice = (salePriceElem.querySelector('.pl-price') || salePriceElem).textContent.trim();
      var discountVariantPrice = priceList.getAttribute('data-discount-variant-price') || '';
      var discountComparePrice = priceList.getAttribute('data-discount-compare-price') || '';
      var doSalePrice = priceList.getAttribute('data-do-sale-price') === 'true';

      if (!doSalePrice || !discountVariantPrice) return; // No private sale — nothing to validate

      var expectedPrice = entitled ? discountVariantPrice.trim() : discountComparePrice.trim();

      // Prefer numeric (cents) comparison to avoid locale/formatting issues
      var displayedPriceCents = parseMoney(displayedPrice);
      var expectedPriceCents = parseMoney(expectedPrice);
      var hasNumericPrices = displayedPriceCents > 0 && expectedPriceCents > 0;
      var pricesMismatch = false;

      if (hasNumericPrices) {
        pricesMismatch = displayedPriceCents !== expectedPriceCents;
      } else if (displayedPrice && expectedPrice) {
        pricesMismatch = displayedPrice !== expectedPrice;
      }

      if (pricesMismatch) {
        var mismatch = {
          productId: productId,
          expected: expectedPrice,
          displayed: displayedPrice,
          entitled: entitled,
          context: entitled ? 'sale' : 'non-sale'
        };
        mismatches.push(mismatch);
        console.warn(
          '[PricingService] Price mismatch for product ' + productId + ': ' +
          'displayed "' + displayedPrice + '" but expected "' + expectedPrice + '" ' +
          '(customer ' + (entitled ? 'entitled' : 'not entitled') + ' to private sale)'
        );
      }

      // Validate compare-at visibility
      var compareAtElem = priceList.querySelector('compare-at-price');
      if (entitled && compareAtElem && compareAtElem.hasAttribute('hidden') && discountComparePrice) {
        var compareDisplayed = (compareAtElem.querySelector('.pl-price') || compareAtElem).textContent.trim();
        if (compareDisplayed !== displayedPrice) {
          // compare-at should be visible for entitled customers when sale is active
          console.warn(
            '[PricingService] Compare-at price hidden for entitled customer on product ' + productId +
            '. Expected compare-at to be visible with value "' + discountComparePrice + '"'
          );
        }
      }
    });

    if (mismatches.length > 0) {
      console.warn('[PricingService] ' + mismatches.length + ' price mismatch(es) detected. See above for details.');
    }

    return mismatches;
  }

  // ---------------------------------------------------------------------------
  // Public API
  // ---------------------------------------------------------------------------

  return {
    /** Apply pricing logic to a single price-list element */
    applyPricing: applyPricing,

    /** Apply pricing to all price-list elements on the page */
    applyAll: applyAll,

    /** Apply pricing within a specific container element */
    applyWithinContainer: applyWithinContainer,

    /** Handle variant change event */
    onVariantChange: onVariantChange,

    /** Fill empty prices on PDP (recovery from render race conditions) */
    fillEmptyPrices: fillEmptyPrices,

    /** Check if current customer has promo entitlement */
    hasPromoEntitlement: hasPromoEntitlement,

    /** Validate displayed prices against source data and return mismatches */
    validate: validate,

    /** Clean stray text nodes from a price element */
    cleanPriceElement: cleanPriceElement
  };
})();

// ---------------------------------------------------------------------------
// Page-load init (GRIT-6631)
// ---------------------------------------------------------------------------

/**
 * Replaces the inline script snippets/price-list.liquid used to output after every
 * <price-list> (94 per collection page). This file is now deferred, so the DOM is
 * fully parsed when it runs. Same steps, once per page:
 *   1. Re-apply pricing for each product with a private sale (do_sale_price),
 *      which is what onVariantChange(productId) did per card.
 *   2. Fill empty PDP prices.
 *   3. Validate. The per-card version only validated the first card on the page;
 *      this checks all of them, so it can log mismatches base never reported.
 */
(function () {
  'use strict';

  function initPageLoad() {
    var appliedProducts = {};
    document.querySelectorAll('price-list[data-do-sale-price="true"]').forEach(function (priceList) {
      var productId = priceList.getAttribute('data-product-id');
      if (!productId || appliedProducts[productId]) return;
      appliedProducts[productId] = true;
      document.querySelectorAll('price-list[data-product-id="' + productId + '"]').forEach(function (pl) {
        PricingService.applyPricing(pl);
      });
    });
    PricingService.fillEmptyPrices();
    PricingService.validate();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initPageLoad);
  } else {
    initPageLoad();
  }
})();
